import * as React from 'react';
import { Alert, Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { fetchApi, makeResetCache, notifyError, notifyResponse } from 'gossamer-front-end';
import {
  AdminApplyEventSeriesSplitRequest,
  AdminPreviewEventSeriesSplitRequest,
  Event,
  EventExtendedView,
  EventRepetition,
  EventSeriesSplitPreview,
  EventSeriesSplitPreviewOutcome,
} from 'cb-model';
import { convertDateTimeFromUTCToLocal } from 'gossamer-unified';

const WEEKDAYS = [
  { id: 0, label: 'Sun' },
  { id: 1, label: 'Mon' },
  { id: 2, label: 'Tue' },
  { id: 3, label: 'Wed' },
  { id: 4, label: 'Thu' },
  { id: 5, label: 'Fri' },
  { id: 6, label: 'Sat' },
];

const OUTCOME_VARIANTS: Record<EventSeriesSplitPreviewOutcome, string> = {
  'remain-original': 'secondary',
  move: 'success',
  create: 'primary',
  'excluded-exception': 'secondary',
  'excluded-cancelled': 'warning',
};

interface EventRepeatPatternModalProps {
  eventId: number;
  firstStartAt: string;
  repeatPattern: unknown;
  show: boolean;
  setShow: (show: boolean) => void;
  onApplied?: () => void;
}

const getLocalStart = (firstStartAt: string) => convertDateTimeFromUTCToLocal(firstStartAt);

const getInitialPattern = (value: unknown, firstStartAt: string): EventRepetition => {
  const parsed =
    typeof value === 'string'
      ? (() => {
          try {
            return JSON.parse(value) as Partial<EventRepetition>;
          } catch {
            return undefined;
          }
        })()
      : value && typeof value === 'object'
        ? (value as Partial<EventRepetition>)
        : undefined;
  const localStart = getLocalStart(firstStartAt);
  return {
    frequency: 'weekly',
    interval_weeks: parsed?.interval_weeks || 1,
    weekdays:
      Array.isArray(parsed?.weekdays) && parsed.weekdays.length ? parsed.weekdays : [new Date(firstStartAt).getDay()],
    end_date: parsed?.end_date || localStart.substring(0, 10),
  };
};

const getRequestEffectiveFrom = (date: string, firstStartAt: string) => {
  const localStart = getLocalStart(firstStartAt);
  const time = localStart.substring(11, 19);
  const value = new Date(`${date}T${time}`);
  return Number.isNaN(value.getTime()) ? firstStartAt : value.toISOString();
};

const getPreviewFromResponse = (response: unknown): EventSeriesSplitPreview | undefined =>
  (response as { seriesSplitPreview?: EventSeriesSplitPreview }).seriesSplitPreview;

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export const EventRepeatPatternModal = (props: EventRepeatPatternModalProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const [pattern, setPattern] = React.useState(() => getInitialPattern(props.repeatPattern, props.firstStartAt));
  const [effectiveDate, setEffectiveDate] = React.useState('');
  const [includeExceptions, setIncludeExceptions] = React.useState(false);
  const [preview, setPreview] = React.useState<EventSeriesSplitPreview>();
  const [previewing, setPreviewing] = React.useState(false);
  const [applying, setApplying] = React.useState(false);

  React.useEffect(() => {
    if (!props.show) return;
    setPattern(getInitialPattern(props.repeatPattern, props.firstStartAt));
    setEffectiveDate(getLocalStart(props.firstStartAt).substring(0, 10));
    setIncludeExceptions(false);
    setPreview(undefined);
  }, [props.show, props.repeatPattern, props.firstStartAt]);

  const updateInputs = (updater: (current: EventRepetition) => EventRepetition) => {
    setPattern((current) => updater(current));
    setPreview(undefined);
  };

  const request = (): AdminPreviewEventSeriesSplitRequest => ({
    eventId: props.eventId,
    effectiveFrom: getRequestEffectiveFrom(effectiveDate, props.firstStartAt),
    proposedPattern: pattern,
    includeExceptions,
  });

  const isValid =
    !!effectiveDate &&
    Number.isInteger(pattern.interval_weeks) &&
    pattern.interval_weeks > 0 &&
    pattern.weekdays.length > 0 &&
    !!pattern.end_date;

  const handlePreview = async () => {
    if (!isValid || previewing) return;
    setPreviewing(true);
    try {
      const response = await fetchApi('POST', '/main/event-series-split-preview', request());
      const repetitionPreview = getPreviewFromResponse(response);
      if (!repetitionPreview) throw new Error('The server did not return a repetition preview');
      setPreview(repetitionPreview);
      notifyResponse(response);
    } catch (error) {
      notifyError(error, 'An error occurred while previewing the repeat pattern');
    }
    setPreviewing(false);
  };

  const handleApply = async () => {
    if (!preview || applying) return;
    setApplying(true);
    try {
      const response = await fetchApi(
        'POST',
        '/main/event-series-split-apply',
        request() as AdminApplyEventSeriesSplitRequest,
      );
      resetCache(Event.id);
      resetCache(Event.id, String(props.eventId));
      resetCache(EventExtendedView.id);
      resetCache(EventExtendedView.id, String(props.eventId));
      notifyResponse(response);
      props.setShow(false);
      props.onApplied?.();
    } catch (error) {
      notifyError(error, 'An error occurred while applying the repeat pattern');
    }
    setApplying(false);
  };

  return (
    <Modal
      show={props.show}
      onHide={() => props.setShow(false)}
      aria-labelledby="event-repeat-pattern-modal-title"
      size="lg"
      fullscreen="md-down"
    >
      <Modal.Header closeButton>
        <Modal.Title id="event-repeat-pattern-modal-title">Split series from this date</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Alert variant="info">
            Events before the effective occurrence stay in the original series. This occurrence becomes the root of a
            new series using the pattern below.
          </Alert>
          <Form.Group className="mb-3" controlId="event-repeat-effective-date">
            <Form.Label>Effective date</Form.Label>
            <Form.Control
              type="date"
              value={effectiveDate}
              min={getLocalStart(props.firstStartAt).substring(0, 10)}
              disabled
            />
            <Form.Text>The selected event is the first occurrence in the new series.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="event-repeat-pattern-interval">
            <Form.Label>Repeat every</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="number"
                min={1}
                step={1}
                value={pattern.interval_weeks}
                onChange={(event) =>
                  updateInputs((current) => ({ ...current, interval_weeks: Number(event.currentTarget.value) }))
                }
                style={{ maxWidth: 100 }}
              />
              <span>week(s)</span>
            </div>
          </Form.Group>
          <Form.Group className="mb-3" controlId="event-repeat-pattern-weekdays">
            <Form.Label>Repeat on</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {WEEKDAYS.map((weekday) => (
                <Form.Check
                  key={weekday.id}
                  inline
                  type="checkbox"
                  id={`event-repeat-pattern-weekday-${weekday.id}`}
                  label={weekday.label}
                  checked={pattern.weekdays.includes(weekday.id)}
                  onChange={(event) =>
                    updateInputs((current) => ({
                      ...current,
                      weekdays: event.currentTarget.checked
                        ? [...current.weekdays, weekday.id].sort((first, second) => first - second)
                        : current.weekdays.filter((value) => value !== weekday.id),
                    }))
                  }
                />
              ))}
            </div>
          </Form.Group>
          <Form.Group className="mb-3" controlId="event-repeat-pattern-end-date">
            <Form.Label>Repeat until</Form.Label>
            <Form.Control
              type="date"
              value={pattern.end_date}
              onChange={(event) => updateInputs((current) => ({ ...current, end_date: event.currentTarget.value }))}
            />
          </Form.Group>
          <Form.Check
            className="mb-3"
            type="checkbox"
            id="event-repeat-include-exceptions"
            label="Move individually changed or manually added future events"
            checked={includeExceptions}
            onChange={(event) => {
              setIncludeExceptions(event.currentTarget.checked);
              setPreview(undefined);
            }}
          />
          {!isValid && <Alert variant="danger">Supply a valid effective date, interval, weekday, and end date.</Alert>}
          <Button variant="secondary" onClick={handlePreview} disabled={!isValid || previewing || applying}>
            {previewing ? 'Previewing…' : 'Preview split'}
          </Button>
        </Form>

        {preview && (
          <div className="mt-4">
            <h5>Preview outcome</h5>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {(Object.keys(OUTCOME_VARIANTS) as EventSeriesSplitPreviewOutcome[]).map((outcome) => (
                <Badge key={outcome} bg={OUTCOME_VARIANTS[outcome]}>
                  {outcome}: {preview.summary[outcome]}
                </Badge>
              ))}
              <Badge bg="dark">Moved bookings: {preview.summary.moved_active_booking_count}</Badge>
              <Badge bg="dark">Moved payments: {preview.summary.moved_payment_count}</Badge>
            </div>
            <div className="table-responsive" style={{ maxHeight: 300 }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Outcome</th>
                    <th>Event date</th>
                    <th>Bookings</th>
                    <th>Payments</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((item, index) => (
                    <tr key={`${item.event_id || 'new'}-${item.starts_at}-${index}`}>
                      <td>
                        <Badge bg={OUTCOME_VARIANTS[item.outcome]}>{item.outcome}</Badge>
                      </td>
                      <td>{formatDateTime(item.starts_at)}</td>
                      <td>{item.active_booking_count}</td>
                      <td>{item.payment_count}</td>
                      <td>{item.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => props.setShow(false)} disabled={previewing || applying}>
          Close
        </Button>
        <Button variant="primary" onClick={handleApply} disabled={!preview || previewing || applying}>
          {applying ? 'Applying…' : 'Split series'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
